"use client";

import { fontWeights } from "@/utils/fonts";
import Image from "next/image";
import { 
  CheckCircle, 
  Monitor, 
  Shield, 
  Globe, 
  Star, 
  Zap, 
  Rocket, 
  Coins, 
  Briefcase,
  Activity,
  Cpu,
  ShoppingBag,
  Building2,
  Check
} from "lucide-react";

const SERVER_URL = process.env.NEXT_PUBLIC_SERVER_URL;

const IconMap: Record<string, any> = {
  monitorIcon: Monitor,
  shieldIcon: Shield,
  globeIcon: Globe,
  starIcon: Star,
  lightningIcon: Zap,
  globeTalentIcon: Globe,
  costIcon: Coins,
  deliveryIcon: Rocket,
  bankIcon: Building2,
  healthcareIcon: Activity,
  lifeSciencesIcon: Activity,
  highTechIcon: Cpu,
  retailIcon: ShoppingBag,
  checkIcon: Check
};


function DynamicIcon({ iconId }: { iconId: string }) {
  const IconComponent = IconMap[iconId] || Check;
  return (
    <span className="rounded-full bg-[#FFF3D9] flex items-center justify-center flex-shrink-0
            w-[25px] 
            sm:w-[30px] 
            lg:w-[35px] 
            xl:w-[40px] 
            h-[25px] 
            sm:h-[30px] 
            lg:h-[35px] 
            xl:h-[40px] 
            ">
      <span className="rounded-full bg-[#FFB000] flex items-center justify-center
              w-[17px] 
              sm:w-[20px] 
              lg:w-[25px] 
              xl:w-[29px] 
              h-[17px] 
              sm:h-[20px] 
              lg:h-[25px] 
              xl:h-[29px] 
              ">
        <IconComponent className="text-white w-[10px] lg:w-[12px] xl:w-[14px] h-[10px] lg:h-[12px] xl:h-[14px]" strokeWidth={3} />
      </span>
    </span>
  );
}

export default function WhyChooseStaffAugmentation({
  title,
  image,
  items,
}: {
  title?: string;
  image?: string;
  items?: { title: string; desc: string; icon?: string }[];
}) {
  const displayImage = image 
    ? (image.startsWith('http') ? image : `${SERVER_URL}/${image}`)
    : '';

  return (
    <section className="bg-white">
      <div className="mx-auto px-4 sm:px-8 py-3 tsm-350:py-4 sm:py-6 md:py-8 lg:py-12 xl:py-16">
        <div className="mx-auto flex flex-col-reverse lg:flex-row max-w-auto xl:max-w-[1393px] items-center justify-center gap-10 lg:gap-16 xl:gap-20 2xl:gap-[100px]">
          {/* Left chart */}
          <div className="flex flex-col lg:flex-row justify-center">
            {image && (
              <Image
                src={displayImage}
                alt="Staff Augmentation Comparison"
                width={428}
                height={411}
                className="max-w-auto tsm-350:max-w-[250px] sm:max-w-[300px] lg:max-w-[280px] xl:max-w-[350px] 2xl:max-w-[428px] h-auto object-contain"
              />
            )}
          </div>

          {/* Right content */}
          <div className="w-full w-auto tsm-400:w-auto sm:w-[576px] md:w-[620px] xl:w-[750px] 2xl:w-[862px] lg:flex-none">
            {title && (
              <h2
                className="text-[#0F172A] tracking-normal xl:tracking-[-2px]
                leading-[30px] 
                sm:leading-[35px] 
                md:leading-[45px] 
                lg:leading-[50px] 
                xl:leading-[70px] 
                2xl:leading-[77px] 
                text-[22px] 
                sm:text-[32px] 
                md:text-[35px] 
                xl:text-[48px] 
                2xl:text-[60px] 
                mb-4
                mb-[20px]
                sm:mb-[30px]
                md:mb-[35px]
                xl:mb-[46px]
                "
                style={{ fontFamily: "Lato", fontWeight: fontWeights.lato.regular }}
                dangerouslySetInnerHTML={{ __html: title }}
              />
            )}

            <div className="flex flex-col gap-3 sm:gap-4 xl:gap-5 2xl:gap-6">
              {items?.map((r, idx) => (
                <div key={idx} className="flex gap-3 lg:gap-[14px] xl:gap-[20px] 2xl:gap-[24px]">
                  <div className="pt-1">
                    <DynamicIcon iconId={r.icon || "checkIcon"} />
                  </div>
                  <div className="flex flex-col gap-1">
                    <div
                      className="text-[1A2B4A] 
                          text-[16px] 
                          sm:text-[18px] 
                          xl:text-[25px] 
                          2xl:text-[28px] 
                          leading-[25px] 
                          xl:leading-[35px] 
                          2xl:leading-[42px]"
                      style={{ fontFamily: "Asap", fontWeight: fontWeights.asap.semiBold }}
                    >
                      {r.title}
                    </div>
                    <div
                      className="text-[1A2B4A] tracking-tight
                          text-[13px] 
                          sm:text-[14px] 
                          xl:text-[17px] 
                          2xl:text-[19px] 
                          leading-[20px] 
                          lg:leading-[25px] 
                          2xl:leading-[32px]"
                      style={{ fontFamily: "Asap", fontWeight: fontWeights.asap.regular }}
                    >
                      {r.desc}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

